import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { graphqlApi } from '../../../services/graphql';

export interface PhoneVerificationProps {
  phoneNumber: string;
  isVerified: boolean;
  onVerified: () => void;
  onPhoneChange: (phone: string) => void;
  /** If true, the phone number was loaded from DB and should be read-only */
  isExisting?: boolean;
  /** If true, hide the verify/authenticate button (phone input still shown) */
  hideVerifyButton?: boolean;
}

const COUNTDOWN_SECONDS = 60;

const COUNTRY_CODES = [
  { code: '+82', label: '🇰🇷 +82' },
  { code: '+1', label: '🇺🇸 +1' },
  { code: '+81', label: '🇯🇵 +81' },
  { code: '+86', label: '🇨🇳 +86' },
  { code: '+44', label: '🇬🇧 +44' },
  { code: '+49', label: '🇩🇪 +49' },
  { code: '+33', label: '🇫🇷 +33' },
  { code: '+61', label: '🇦🇺 +61' },
  { code: '+65', label: '🇸🇬 +65' },
  { code: '+84', label: '🇻🇳 +84' },
  { code: '+66', label: '🇹🇭 +66' },
  { code: '+63', label: '🇵🇭 +63' },
  { code: '+62', label: '🇮🇩 +62' },
  { code: '+60', label: '🇲🇾 +60' },
  { code: '+91', label: '🇮🇳 +91' },
];

/** Extract the country code from a full phone number */
function extractCountryCode(phone: string): string {
  for (const { code } of COUNTRY_CODES) {
    if (phone.startsWith(code)) return code;
  }
  return '+82';
}

/** Extract the local number (digits after country code) */
function extractLocalNumber(phone: string, countryCode: string): string {
  return phone.startsWith(countryCode) ? phone.slice(countryCode.length) : phone.replace(/^\+\d+/, '');
}

const PhoneVerification: React.FC<PhoneVerificationProps> = ({
  phoneNumber,
  isVerified,
  onVerified,
  onPhoneChange,
  isExisting = false,
  hideVerifyButton = false,
}) => {
  const { t } = useTranslation('profile');

  const [isLocked, setIsLocked] = useState(isExisting && !!phoneNumber.trim());
  const [justVerified, setJustVerified] = useState(false);

  const [verificationCode, setVerificationCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(0);
  const [countryCode, setCountryCode] = useState(() => extractCountryCode(phoneNumber));
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Countdown timer effect
  useEffect(() => {
    if (countdown > 0) {
      timerRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            if (timerRef.current) clearInterval(timerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [countdown]);

  const handleSendCode = useCallback(async () => {
    if (!phoneNumber.trim() || isSending) return;

    setIsSending(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const result = await graphqlApi.sendPhoneVerification(phoneNumber);
      if (result.success) {
        setCodeSent(true);
        setCountdown(COUNTDOWN_SECONDS);
        setSuccessMessage(t('myProfile.form.codeSent'));
        setVerificationCode('');
      } else {
        setError(t('myProfile.form.codeSendFailed'));
      }
    } catch {
      setError(t('myProfile.form.codeSendFailed'));
    } finally {
      setIsSending(false);
    }
  }, [phoneNumber, isSending, t]);

  const handleVerifyCode = useCallback(async () => {
    if (!verificationCode.trim() || isVerifying) return;

    setIsVerifying(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const result = await graphqlApi.confirmPhoneVerification(phoneNumber, verificationCode);
      if (result.success) {
        onVerified();
        setSuccessMessage(t('myProfile.form.phoneVerified'));
        setCodeSent(false);
        setCountdown(0);
        setIsLocked(true); // Lock the field after successful verification
        setJustVerified(true);
      } else {
        setError(t('myProfile.form.codeInvalid'));
      }
    } catch {
      setError(t('myProfile.form.codeInvalid'));
    } finally {
      setIsVerifying(false);
    }
  }, [verificationCode, phoneNumber, isVerifying, onVerified, t]);

  // If phone number already exists, show as read-only with Change button
  if (isLocked) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <input
            type="tel"
            value={phoneNumber}
            readOnly
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed focus:outline-none"
          />
          {(isVerified || justVerified) && (
            <span className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg whitespace-nowrap">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              {t('myProfile.form.phoneVerified')}
            </span>
          )}
          {!hideVerifyButton && (
            <button
              type="button"
              onClick={() => setIsLocked(false)}
              className="px-3 py-2 text-sm font-medium text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors whitespace-nowrap"
            >
              {t('myProfile.form.phoneChange')}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Phone input + Authenticate button */}
      <div className="flex items-center gap-2">
        <select
          value={countryCode}
          onChange={(e) => {
            const newCode = e.target.value;
            const local = extractLocalNumber(phoneNumber, countryCode);
            setCountryCode(newCode);
            onPhoneChange(newCode + local);
            if (codeSent) {
              setCodeSent(false);
              setVerificationCode('');
              setCountdown(0);
              setError(null);
              setSuccessMessage(null);
            }
          }}
          className="min-w-[6rem] px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-l-lg focus:ring-1 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
        >
          {COUNTRY_CODES.map((c) => (
            <option key={c.code} value={c.code}>{c.label}</option>
          ))}
        </select>
        <input
          type="tel"
          value={extractLocalNumber(phoneNumber, countryCode)}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, '');
            const fullNumber = countryCode + digits;
            onPhoneChange(fullNumber);
            if (codeSent) {
              setCodeSent(false);
              setVerificationCode('');
              setCountdown(0);
              setError(null);
              setSuccessMessage(null);
            }
          }}
          className="flex-1 px-3 py-2 text-sm border-y border-r border-gray-300 rounded-r-lg focus:ring-1 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          placeholder={t('myProfile.form.phonePlaceholder')}
        />
        {!hideVerifyButton && (
          <button
            type="button"
            onClick={handleSendCode}
            disabled={!phoneNumber.trim() || isSending || countdown > 0}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {isSending
              ? '...'
              : countdown > 0
                ? `${countdown}s`
                : t('myProfile.form.phoneAuthenticate')}
          </button>
        )}
      </div>

      {/* Success message */}
      {successMessage && !error && (
        <p className="text-xs text-green-600">{successMessage}</p>
      )}

      {/* Verification code input (appears after code sent) */}
      {codeSent && (
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={verificationCode}
            onChange={(e) => {
              // Only allow digits, max 6
              const val = e.target.value.replace(/\D/g, '').slice(0, 6);
              setVerificationCode(val);
              setError(null);
            }}
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            placeholder={t('myProfile.form.verificationCodePlaceholder')}
            maxLength={6}
          />
          <button
            type="button"
            onClick={handleVerifyCode}
            disabled={verificationCode.length !== 6 || isVerifying}
            className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {isVerifying ? '...' : t('myProfile.form.verifyCode')}
          </button>
        </div>
      )}

      {/* Error message */}
      {error && (
        <p className="text-xs text-red-500">{error}</p>
      )}
    </div>
  );
};

export default PhoneVerification;
